import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChildren
} from '@angular/core';
import * as Popper from '@popperjs/core';
import * as _ from 'lodash-es';
import {Subject, timer} from 'rxjs';
import {switchMap, takeUntil, tap} from 'rxjs/operators';
import {destroy$} from '../destroyable';
import {ModalService} from '../modal';
import {OverlayService} from '../overlay';
import {createPopperDropdownOffsetModifier} from '../popper-utils/modifiers/popper-dropdown-offset';
import {createPopperReferenceWideModifier} from '../popper-utils/modifiers/popper-reference-wide';
import {PopperOptions} from '../popper-utils/popper-options';
import {AutocompleteConfig, getAutocompleteDisplayValue} from './autocomplete-config';

const EMPTY_CLASS = '-empty';

export interface DisplayVariant<VariantType = any> {
    variant: VariantType;
    displayValue?: string;
}

@Component({
    selector: 'ul[autocompleteList]',
    templateUrl: './autocomplete-list.component.html',
    styleUrls: ['./autocomplete-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AutocompleteListComponent<VariantType = any, DisplayVariantType extends DisplayVariant = DisplayVariant>
    implements OnInit, OnDestroy {

    @Input()
    public inputElement: ElementRef;

    @Input()
    public referenceElement: ElementRef;

    @Input()
    public config: AutocompleteConfig;

    @Output()
    public onSelect = new EventEmitter<VariantType>();

    @Output()
    public onClose = new EventEmitter<VariantType>();

    @HostBinding(`class.${EMPTY_CLASS}`)
    public isEmpty: boolean = true;

    @ViewChildren('item', {read: ElementRef})
    public itemQueryList: QueryList<ElementRef>;

    public selectedVariant: VariantType;

    public displayVariants: DisplayVariantType[] = [];

    public readonly destroy$ = destroy$();

    public readonly changeDetectionSafeItems$ = new Subject<QueryList<ElementRef>>();
    public readonly changeDetectionSafeItemsUpdate$ = this.changeDetectionSafeItems$.pipe(
        switchMap(list => list.changes)
    );

    protected popper: Popper.Instance;

    protected _variants: VariantType[];

    constructor(
        protected listElement: ElementRef,
        protected modalService: ModalService,
        protected clickOutsideService: OverlayService,
        protected injector: Injector
    ) {}

    public ngOnInit(): void {
        this.updateVariants();
        this.createOverlay();
        this.initPopper();
        this.listenToWatchlistItems();
    }

    public ngOnDestroy(): void {
        this.destroyPopper();
    }

    public selectVariant(variant?: VariantType): void {
        variant = variant || this.selectedVariant;

        this.onSelect.emit(variant);
        this.inputElement.nativeElement
            .focus();

        this.close();
    }

    public prevVariant(): void {
        const index = this.getSelectedVariantIndex() - 1;

        if (index >= 0) {
            this.selectedVariant = this._variants[index];
        }
    }

    public nextVariant(): void {
        const index = this.getSelectedVariantIndex() + 1;

        if (index < this._variants.length) {
            this.selectedVariant = this._variants[index];
        }
    }

    public setVariants(variants: VariantType[]): void {
        if (this._variants !== variants) {
            this._variants = variants;
            this.updateVariants();
        }
    }

    public close(): void {

        // Notify subscribers before the component will be destroyed, because destroy$ may unsubscribe listeners.
        this.onClose.emit();

        this.modalService.close(this);
    }

    protected updateVariants(): void {
        this.displayVariants = this._variants.map<DisplayVariant>(
            variant => ({
                variant,
                displayValue: getAutocompleteDisplayValue(variant, this.config)
            })
        ) as unknown as DisplayVariantType[];

        this.selectedVariant = _.head(this._variants);

        this.updateIsEmpty();
    }

    protected updateIsEmpty(): void {
        this.isEmpty = !this.displayVariants.length;
    }

    protected initPopper(popperOptions: PopperOptions = {}): void {
        this.popper = Popper.createPopper(
            this.referenceElement || this.inputElement.nativeElement,
            this.listElement.nativeElement,
            {
                placement: 'bottom-start',
                modifiers: [
                    createPopperDropdownOffsetModifier(),
                    createPopperReferenceWideModifier()
                ],
                ...popperOptions
            }
        );

        this.updatePosition();

        this.changeDetectionSafeItemsUpdate$.pipe(
            tap(() => this.updatePosition())
        ).subscribe();
    }

    protected updatePosition(): void {
        if (this.popper) {
            this.popper.update();
        }
    }

    protected destroyPopper(): void {
        if (this.popper) {
            this.popper.destroy();
        }
    }

    protected getSelectedVariantIndex(): number {
        const selectedVariant = this.selectedVariant;

        return _.findIndex(
            this._variants,
            variant => variant === selectedVariant
        );
    }

    protected createOverlay(): void {
        this.clickOutsideService.create({
            target: this.modalService.getByInstance(this)
        }).subscribe(() => this.close());
    }

    private listenToWatchlistItems(): void {
        timer(0).pipe(
            tap(() => this.changeDetectionSafeItems$.next(this.itemQueryList)),
            takeUntil(this.destroy$)
        ).subscribe();
    }

}
